import { get, post, put, del } from './requests.js'

// Taxonomy ID sets: partner-specific segment-ID formats, managed from the
// Segment Ids section and offered to every taxonomy's Segment IDs dropdown. A
// set's `slug` is the mapping space its IDs occupy on an audience, which is the
// value a distribution stores in `mapping_ids`.
//
// `transformPayload: false` throughout - the API speaks snake_case here
// (`starts_with_letter`, `taxonomy_ids`) and camelizing it would only mean
// mapping the names back at every call site.
const CONFIG = { transformPayload: false, removeTrailingSlash: true }

export const getTaxonomyIdSets = () => get(['taxonomy-id-sets'], CONFIG, { limit: 1000 })

export const getTaxonomyIdSet = (id) => get(['taxonomy-id-sets', id], CONFIG)

// Payload is { name, description, alphanumeric, starts_with_letter, size }.
// Creating a set defines the format only - IDs reach audiences as they are
// created, and reach existing ones through backfill below.
export const createTaxonomyIdSet = (payload) => post(['taxonomy-id-sets'], CONFIG, payload)

export const updateTaxonomyIdSet = (id, payload) => put(['taxonomy-id-sets', id], CONFIG, payload)

export const deleteTaxonomyIdSet = (id) => del(['taxonomy-id-sets', id], CONFIG)

// Creating a set answers as soon as the set exists; filling every pre-existing
// taxonomy segment carries on afterwards, because holding the request open for
// it draws a 504 from the load balancer on any real database. Progress lands on
// the set itself, so following it means reading the set back.
const POLL_INTERVAL_MS = 2000

// How long to keep watching before handing the wait back to the caller. The
// fill is not cancelled - it keeps running server-side and the set records how
// it ends - this only bounds how long a dialog sits open.
const POLL_TIMEOUT_MS = 3 * 60 * 1000

const FILL_RUNNING = 'running'

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// Reads the set back until its fill stops running.
//
// Resolves with { idSet, timedOut }. A timeout is not a failure: the work is
// still going, so the caller reports that rather than claiming it finished or
// that it broke. A read that fails is retried rather than ending the wait -
// one blip should not look like a finished fill.
export const waitForTaxonomyIdSetFill = async (id, { onPoll } = {}) => {
  const startedAt = Date.now()
  let idSet = null

  for (;;) {
    try {
      idSet = await getTaxonomyIdSet(id)
      if (onPoll) onPoll(idSet)
      if (idSet?.fill_status !== FILL_RUNNING) return { idSet, timedOut: false }
    } catch (error) {
      // keep waiting - the fill is unaffected by our failure to observe it
    }

    if (Date.now() - startedAt >= POLL_TIMEOUT_MS) return { idSet, timedOut: true }
    await delay(POLL_INTERVAL_MS)
  }
}
